<template>
  <div class="connexion">
    <div class="login-card">
      <div class="form-title" style="text-align: center;">Inscription</div>
      <div style="text-align: center;">100% gratuite à Mes Meilleurs Menus</div>
      <form @submit.prevent="submitForm" class="form">
        <transition name="slide-fade" mode="out-in">
          <div :key="currentStep">
            <!-- STEP 1 : EMAIL -->
            <div v-if="currentStep === 1" class="form-group">
              <label for="email">Mon Adresse Email</label>
              <input type="email" v-model="email" class="form-input" required />
              <div v-if="!isEmailValid && !apiErrors.email" class="error">L'email est invalide</div>
              <div v-else-if="apiErrors.email" class="error">{{ apiErrors.email }}</div>
            </div>

            <!-- STEP 2 : MOT DE PASSE -->
            <div v-else-if="currentStep === 2">
              <div class="form-group">
                <label for="password">Mon Mot de Passe</label>
                <input type="password" v-model="password" class="form-input" required />
              </div>

              <div class="form-group">
                <label for="confirmPassword">Encore Mon Mot de Passe</label>
                <input type="password" v-model="confirmPassword" class="form-input" required />
                <div v-if="!isConfirmPasswordValid" class="error">
                  Les mots de passe ne correspondent pas
                </div>
                <div v-if="triedPasswordStep && apiErrors.password" class="error">
                  {{ apiErrors.password }}
                </div>
              </div>
            </div>

            <!-- STEP 3 : PRENOM -->
            <div v-else-if="currentStep === 3" class="form-group">
              <label for="name">Mon Prénom</label>
              <input type="text" v-model="name" class="form-input" required />
            </div>

            <!-- STEP 4 : PAYS + CODE POSTAL -->
            <div v-else-if="currentStep === 4">
              <div class="form-group">
                <label for="country_code">Mon Pays</label>
                <CountrySelect v-model="countryCode" class="form-input" required />
              </div>

              <div class="form-group">
                <label for="zipcode">Mon Code Postal</label>
                <input type="text" v-model="zipcode" class="form-input" required />
                <div v-if="!isZipcodeValid" class="error">
                  Le code postal doit contenir {{ zipcodeNbDigitsRequired }} chiffres
                </div>
                <div v-if="apiErrors.zipcode" class="error">{{ apiErrors.zipcode }}</div>
              </div>

              <div class="form-group">
                Nous utilisons votre code postal pour proposer des menus avec des ingrédients de vos régions
              </div>
            </div>

            <!-- STEP 5 : ANNEE DE NAISSANCE -->
            <div v-else-if="currentStep === 5" class="form-group">
              <label for="yearOfBirth">Mon Année de Naissance</label>
              <input type="number" v-model="yearOfBirth" class="form-input" required />
              <div v-if="!isYearOfBirthOldEnough" class="error">
                Désolé, nous ne pouvons pas accepter les personnes de moins de 5 ans
              </div>
              <div v-if="!isYearOfBirthYoungEnough" class="error">
                L'année de naissance doit être supérieure à 1900
              </div>
              <div v-if="!isYearOfBirthValid" class="error">
                L'année de naissance doit être un nombre à 4 chiffres
              </div>
            </div>

            <!-- STEP 6 : GENRE -->
            <div v-else-if="currentStep === 6" class="form-group">
              <label for="gender">Mon Genre</label>
              <select v-model="gender" class="form-input" required>
                <option value="" disabled hidden>Choisissez un genre</option>
                <option value="male">Homme</option>
                <option value="female">Femme</option>
              </select>
            </div>

            <!-- STEP 7 : TAILLE -->
            <div v-else-if="currentStep === 7" class="form-group">
              <label for="height">Ma Taille en cm</label>
              <input type="number" v-model="height" class="form-input" required />
              <div v-if="!isHeightValid" class="error">
                La taille doit être comprise entre 100cm et 230cm
              </div>
            </div>

            <!-- STEP 8 : POIDS -->
            <div v-else-if="currentStep === 8">
              <div class="form-group">
                <label for="weight">Mon Poids en kg</label>
                <input type="number" v-model="weight" class="form-input" required />
              </div>

              <div class="form-group">
                <input type="checkbox" v-model="wantToLooseWeight" />
                Je souhaite perdre du poids
              </div>
              <div v-if="!isWeightValid" class="error">
                Le poids doit être compris entre 30kg et 250kg
              </div>
            </div>

            <!-- STEP 9 : ACTIVITE PHYSIQUE -->
            <div v-else-if="currentStep === 9" class="form-group">
              <label class="form-label">
                Temps d'activité physique moyenne par jour
                <a href="#" class="infos"
                  v-b-popover.hover.top="`Connaitre votre temps moyen d'activité physique par jour nous aide à calculer vos besoins en apport calorique`"
                  title="Temps moyen d'activité physique par jour">?</a>
              </label>

              <div class="grid">
                <div>
                  Faible intensité
                  <a href="#" class="infos"
                    v-b-popover.hover.top="`Exemple: Position debout, attente passive, cuisiner, travail statique...`"
                    title="Temps moyen d'activité physique de faible intensité par jour">?</a>
                </div>

                <InteractiveProgressBar v-model="lowIntensityActivity" :max="8 * 60" :height="25"
                  :label="humanizeActivity(lowIntensityActivity)" :step="5" color="#7ab228" />

                <div>
                  Intensité modérée
                  <a href="#" class="infos"
                    v-b-popover.hover.top="`Exemple: Industrie de production, menuiserie, yoga, marche...`"
                    title="Temps moyen d'activité physique d'intensité modérée par jour">?</a>
                </div>

                <InteractiveProgressBar v-model="mediumIntensityActivity" :max="8 * 60" :height="25"
                  :label="humanizeActivity(mediumIntensityActivity)" :step="5" color="#e7b642" />

                <div>
                  Forte intensité
                  <a href="#" class="infos"
                    v-b-popover.hover.top="`Exemple: Travaux du bâtiment, bêchage, ménage intensif, sport en compétition, footing...`"
                    title="Temps d'activité physique moyenne de forte intensité par jour">?</a>
                </div>

                <InteractiveProgressBar v-model="highIntensityActivity" :max="8 * 60" :height="25"
                  :label="humanizeActivity(highIntensityActivity)" :step="5" color="#ee5e5b" />
              </div>
            </div>

            <!-- STEP 10 : CGU -->
            <div v-else-if="currentStep === 10">
              <div class="form-group">J'y suis presque...</div>
              <div class="form-group">
                <div>
                  <input type="checkbox" v-model="cgu" />
                  J'accepte les
                  <a href="http://frontend-v2.mmmenus.fr/politique-de-confidentialite" target="_blank">conditions
                    générales d'utilisation</a>
                </div>
                <div v-if="cgu === false" class="error">Veuillez accepter les CGU</div>
              </div>
            </div>
          </div>
        </transition>

        <div class="form-group" style="text-align: center;">
          <button v-if="currentStep < totalSteps" class="btn" :disabled="!currentIsValid" @click="goToNextStep"
            type="button">
            Continuer
          </button>
          <button v-else class="btn" type="submit" :disabled="loading || !currentIsValid">
            <Loader v-if="loading" /> Je m'inscris
          </button>
        </div>
      </form>

      <div class="forget-password">
        <router-link to="/connection">J'ai déjà un compte ? Me connecter</router-link>
      </div>

      <div v-if="successMessage" class="success" :style="{ backgroundColor: successColor }">
        {{ successMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import { api } from "@/api";
import { userState } from "@/utils/store";
import Loader from "@/components/Loader.vue";
import CountrySelect from "@/components/CountrySelect.vue";
import InteractiveProgressBar from "@/components/InteractiveProgressBar.vue";

export default {
  name: "Inscription",
  components: { Loader, CountrySelect, InteractiveProgressBar },

  data() {
    return {
      email: "",
      password: "",
      confirmPassword: "",
      name: "",
      yearOfBirth: 1975,
      gender: "",
      height: 170,
      weight: 70,
      zipcode: "",
      countryCode: "fr",
      wantToLooseWeight: false,
      lowIntensityActivity: 60,
      mediumIntensityActivity: 60,
      highIntensityActivity: 60,
      successMessage: "",
      successColor: "",
      loading: false,
      cgu: null,
      currentStep: 1,
      totalSteps: 10,

      apiErrors: {},

      // 🔥 Ajout essentiel : flag pour afficher l’erreur APRES clic uniquement
      triedPasswordStep: false,
    };
  },

  computed: {
    isEmailValid() {
      return this.email === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
    },
    isPasswordValid() {
      return this.password === "" || this.password.length >= 5;
    },
    isConfirmPasswordValid() {
      return this.confirmPassword === "" || this.confirmPassword === this.password;
    },
    isZipcodeValid() {
      if (!this.zipcode) return true;
      if (!this.countryCode) return true;
      if (!this.countryCode == "other") return true;

      const patterns = {
        fr: /^\d{5}$/,
        be: /^\d{4}$/,
        us: /^\d{5}(-\d{4})?$/,
        ca: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
        gb: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
        de: /^\d{5}$/,
        es: /^\d{5}$/,
        it: /^\d{5}$/,
        jp: /^\d{3}-\d{4}$/,
      };

      const pattern = patterns[this.countryCode?.toLowerCase()];
      if (!pattern) return true;
      return pattern.test(this.zipcode.trim());
    },
    isYearOfBirthValid() {
      return this.yearOfBirth === "" || /^\d{4}$/.test(this.yearOfBirth);
    },
    isYearOfBirthOldEnough() {
      if (!this.isYearOfBirthValid) return true;
      const year = new Date().getFullYear();
      return this.yearOfBirth < year - 5;
    },
    isYearOfBirthYoungEnough() {
      if (!this.isYearOfBirthValid) return true;
      return this.yearOfBirth >= 1900;
    },
    isHeightValid() {
      return this.height === "" || (this.height > 100 && this.height < 230);
    },
    isWeightValid() {
      return this.weight === "" || (this.weight > 30 && this.weight < 250);
    },

    currentIsValid() {
      switch (this.currentStep) {
        case 1:
          return this.isEmailValid && this.email !== "";
        case 2:
          return (
            this.isPasswordValid &&
            this.password !== "" &&
            this.isConfirmPasswordValid &&
            this.confirmPassword !== ""
          );
        case 3:
          return this.name !== "";
        case 4:
          return this.countryCode !== "" && this.zipcode !== "" && this.isZipcodeValid;
        case 5:
          return (
            this.yearOfBirth !== "" &&
            this.isYearOfBirthOldEnough &&
            this.isYearOfBirthYoungEnough &&
            this.isYearOfBirthValid
          );
        case 6:
          return this.gender !== "";
        case 7:
          return this.height !== "" && this.isHeightValid;
        case 8:
          return this.weight !== "" && this.isWeightValid;
        case 9:
          return (
            this.lowIntensityActivity !== "" &&
            this.mediumIntensityActivity !== "" &&
            this.highIntensityActivity !== ""
          );
        case 10:
          return this.cgu === true;
        default:
          return false;
      }
    },

    zipcodeNbDigitsRequired() {
      const patterns = {
        fr: 5,
        be: 4,
        us: 5,
        ca: 6,
        gb: 6,
        de: 5,
        es: 5,
        it: 5,
        jp: 7,
      };
      return patterns[this.countryCode?.toLowerCase()] || 0;
    },
  },

  methods: {
    goToNextStep() {
      if (!this.currentIsValid) return;

      // 🔥 Lorsqu’on valide l’étape 2, on active l’affichage des erreurs API
      if (this.currentStep === 2) {
        this.triedPasswordStep = true;
      }

      // 🔥 Validation du mot de passe avec l’API
      if (this.currentStep === 2) {
        this.loading = true;

        api
          .post("/auth/check-password/", { password: this.password })
          .then(() => {
            // Effacer erreurs
            this.$set(this.apiErrors, "password", null);

            this.currentStep++;
          })
          .catch((error) => {
            const data = error?.response?.data;

            // 🔥 Essentiel : utiliser $set pour la réactivité Vue 2
            if (data?.password) {
              this.$set(
                this.apiErrors,
                "password",
                Array.isArray(data.password) ? data.password.join(" ") : data.password
              );
            }
          })
          .finally(() => {
            this.loading = false;
          });

        return;
      }

      this.currentStep++;
    },

    submitForm() {
      this.loading = true;
      this.apiErrors = {};
      this.successMessage = "";
      userState.clear();

      api
        .post("/auth/signup/", {
          email: this.email,
          password: this.password,
          name: this.name,
          year_of_birth: this.yearOfBirth,
          gender: this.gender,
          height: this.height,
          weight: this.weight,
          zipcode: this.zipcode,
          country_code: this.countryCode,
          want_to_loose_weight: this.wantToLooseWeight,
          low_intensity_activity: this.lowIntensityActivity,
          medium_intensity_activity: this.mediumIntensityActivity,
          high_intensity_activity: this.highIntensityActivity,
        })
        .then((response) => {
          userState.setMe(response.data.user);
          userState.setTokens(response.data.access, response.data.refresh);
          this.successMessage = "Inscription réussie";
          this.successColor = "green";
          this.$router.push("/mes-preferences");
        })
        .catch((error) => {
          this.apiErrors = {};
          this.successMessage = "";
          this.successColor = "red";

          const data = error?.response?.data;

          if (data && typeof data === "object") {
            for (const [key, value] of Object.entries(data)) {
              this.$set(
                this.apiErrors,
                key,
                Array.isArray(value) ? value.join(" ") : String(value)
              );
            }

            const stepMapping = {
              email: 1,
              password: 2,
              name: 3,
              zipcode: 4,
              year_of_birth: 5,
              gender: 6,
              height: 7,
              weight: 8,
            };

            const firstErrorField = Object.keys(data)[0];
            this.currentStep = stepMapping[firstErrorField] || 1;
          } else {
            this.successMessage = "Erreur lors de l'inscription. Veuillez réessayer.";
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    humanizeActivity(time) {
      const hours = Math.floor(time / 60);
      const minutes = time % 60;
      return `${hours} heures ${minutes} minutes`;
    },
  },
};
</script>



<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.connexion {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.login-card {
  max-width: 500px;
  width: 100%;
  height: 600px;
  font-family: "TangoSans", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #436f0e;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: 0.3s;
  padding: 16px;
  background-color: #b5d088;
  border-radius: 5px;
  font-size: 18px;
  display: flex;
  flex-direction: column;
}

.form {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.btn {
  background-color: rgb(0, 76, 64);
  color: white;
  border: none;
  transition: 0.2s ease-in-out;
}

.btn:hover {
  background-color: rgb(161, 201, 0);
  color: white;
}

.btn:disabled {
  background-color: #ccc;
  color: #666;
  cursor: not-allowed;
}

.error,
.success {
  margin-top: 10px;
  padding: 10px;
  border-radius: 5px;
}

.error {
  background-color: #f44336;
  color: white;
}

.success {
  color: white;
}

.forget-password {
  margin-top: 10px;
  text-align: center;
}

.forget-password a {
  color: #007bff;
  text-decoration: none;
}

.forget-password a:hover {
  text-decoration: underline;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 10px;
}
</style>
